import { Inject, Injectable } from '@angular/core';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { ProcedureRulesParser } from '@shared/services/rx-rules-helper/procedure-rules.parser';
import { PROCEDURE_RULES_PARSER } from '@shared/services/rx-rules-helper/parser-tokens';
import { RxIndicationRulesService } from '@shared/services/rx-rules-helper/rx-indication-rules.service';
import { ProcedureMapCheckedValue } from '@core/procedure-helpers/models/procedure-map-composites';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { Maybe } from '@/types/globals';
import { isDentureRemovableFullDentureImplantBased } from '@shared/utils/procedure-type-util';
import { OrderQuery } from '@modules/order/state/order-query';
import { fromUnitType, fromUnitTypeInBridge, IntuitiveIndication, toUnitTypeOrDefault } from '../models/intuitive-indication';
import { IntuitiveBridge } from '../models/intuitive-bridge';
import { indicationsWithoutDetails } from '../models/consts';

/**
 * In application code prefer using
 * {@link RxIntuitiveIndicationRulesService#getDefaultIndication} and
 * {@link RxIntuitiveIndicationRulesService#getDefaultUnitType}
 * Do not import these constants anywhere except test mocks.
 */
export const DEFAULT_INDICATION = IntuitiveIndication.Regular;
export const DEFAULT_UNIT_TYPE = UnitTypes.Regular;

@Injectable({ providedIn: 'root' })
export class RxIntuitiveIndicationRulesService {
	constructor(
		@Inject(PROCEDURE_RULES_PARSER) private readonly procedureRulesParser: ProcedureRulesParser<UnitTypes>,
		private readonly rxIndicationRulesService: RxIndicationRulesService,
		private readonly orderQuery: OrderQuery
	) {}

	shouldShowToothEditorProperties({ indicationId }: { indicationId: IntuitiveIndication | undefined | null }): boolean {
		if (indicationId === null || indicationId === undefined) {
			return false;
		}

		return !this.orderQuery.isGlidewellOrder && !indicationsWithoutDetails.includes(indicationId);
	}

	/**
	 * Get all the indications that are possible for a given procedure map and rx configuration.
	 */
	getProcedureIndications(
		procedureMap: Maybe<ProcedureMapCheckedValue>,
		rxConfiguration: Maybe<RxConfiguration>,
		withIteroModelling: boolean,
		withBridges: boolean
	): IntuitiveIndication[] {
		let unitTypeIds = this.procedureRulesParser.getUnitTypeIdsByProcedure(procedureMap, rxConfiguration);

		if (!withIteroModelling) {
			unitTypeIds = this.rxIndicationRulesService.removeModelingSpecificUnitTypes(unitTypeIds);
		}

		let indications: IntuitiveIndication[] = unitTypeIds
			.map(unitTypeId => fromUnitType(unitTypeId))
			.filter((indication): indication is IntuitiveIndication => indication !== null && indication !== IntuitiveIndication.Regular);

		if (withBridges) {
			indications.push(IntuitiveIndication.Bridge);
		}

		indications = this.sort(indications);

		return indications;
	}
	/**
	 * Get the indications that are possible for all the selected teeth.
	 */
	getCommonIndicationsForTeeth(
		teeth: Tooth[],
		procedureMap: Maybe<ProcedureMapCheckedValue>,
		rxConfiguration: Maybe<RxConfiguration>,
		withIteroModelling: boolean,
		withBridges: boolean
	): IntuitiveIndication[] {
		if (teeth.length === 0) {
			return [];
		}

		const unitTypesPerTooth = teeth.map(tooth =>
			this.rxIndicationRulesService.getProcedureFlowUnitType(tooth, procedureMap, rxConfiguration, withIteroModelling, false)
		);

		const commonForAll = unitTypesPerTooth
			.reduce((result, toothIndication) => result.filter(t => toothIndication.includes(t)), unitTypesPerTooth[0])
			.map(unitTypeId => fromUnitType(unitTypeId))
			.filter((indication): indication is IntuitiveIndication => indication !== null);

		if (withBridges) {
			commonForAll.push(IntuitiveIndication.Bridge);
		}

		return commonForAll;
	}

	getDefaultUnitType(): UnitTypes {
		return toUnitTypeOrDefault(this.getDefaultIndication(), DEFAULT_UNIT_TYPE);
	}

	getDefaultIndication(): IntuitiveIndication {
		if (this.isDentureImplantBased()) {
			return IntuitiveIndication.Missing;
		}

		return DEFAULT_INDICATION;
	}

	getBridgeIndicationForDiagram(tooth: Tooth): IntuitiveIndication {
		if (tooth.ToothInBridgeTypeID) {
			return fromUnitTypeInBridge(tooth.ToothInBridgeTypeID) ?? DEFAULT_INDICATION;
		} else {
			return DEFAULT_INDICATION;
		}
	}

	getIndicationForDiagram(indication: IntuitiveIndication | null | undefined): IntuitiveIndication {
		if (!indication || indication === DEFAULT_INDICATION) {
			return this.getDefaultIndication();
		}

		return indication ?? this.getDefaultIndication();
	}

	getUnitTypeIndicationForDiagram(unitType: UnitTypes | null | undefined): IntuitiveIndication {
		return this.getIndicationForDiagram(fromUnitType(unitType));
	}

	isDefaultUnitType(unitType: UnitTypes | null | undefined): boolean {
		return this.getUnitTypeIndicationForDiagram(unitType) === this.getDefaultIndication();
	}

	isDefaultIndication(indication: IntuitiveIndication | null | undefined): boolean {
		return this.getIndicationForDiagram(indication) === this.getDefaultIndication();
	}

	/**
	 * Static union of bridge unit types (inner ∪ outer). Procedure-agnostic; useful as a base set.
	 */
	getBridgeIndications(): IntuitiveIndication[] {
		const unitTypes = new Set([...this.rxIndicationRulesService.outerBridgeTypes, ...this.rxIndicationRulesService.innerBridgeTypes]);

		return this.sort(
			[...unitTypes]
				.map(id => fromUnitTypeInBridge(id))
				.filter((indication): indication is IntuitiveIndication => indication !== null)
		);
	}

	/**
	 * Get all bridge indications visible for a given procedure map (procedure-agnostic of tooth position).
	 *
	 * Mirrors {@link getProcedureIndications} but for bridge unit types: the result is the static
	 * inner ∪ outer set filtered by what the procedure rules allow.
	 */
	getBridgeProcedureIndications(
		procedureMap: Maybe<ProcedureMapCheckedValue>,
		rxConfiguration: Maybe<RxConfiguration>
	): IntuitiveIndication[] {
		const unitTypeIds = this.rxIndicationRulesService.getProcedureUnitTypeIdsForBridge(procedureMap, rxConfiguration);

		return this.sort(
			unitTypeIds.map(id => fromUnitTypeInBridge(id)).filter((indication): indication is IntuitiveIndication => indication !== null)
		);
	}

	/**
	 * Get the bridge indications that are possible for ALL the selected teeth inside the bridge.
	 *
	 * For each selected tooth we resolve its outer/inner position (relative to {@link bridge}) and
	 * fetch the procedure-allowed bridge unit types for that position; then we intersect across teeth
	 * (mirrors the non-bridge {@link getCommonIndicationsForTeeth}).
	 */
	getCommonBridgeIndicationsForTeeth(
		selectedTeeth: Tooth[],
		bridge: Maybe<IntuitiveBridge>,
		procedureMap: Maybe<ProcedureMapCheckedValue>,
		rxConfiguration: Maybe<RxConfiguration>
	): IntuitiveIndication[] {
		if (!bridge || selectedTeeth.length === 0) {
			return [];
		}

		const bridgeUnitTypesPerTooth = selectedTeeth.map(tooth => {
			const isOuterTooth = tooth.ToothID === bridge.outerLeftToothId || tooth.ToothID === bridge.outerRightToothId;

			return this.rxIndicationRulesService.getProcedureFlowUnitTypeIdsForBridge(procedureMap, tooth, isOuterTooth, rxConfiguration);
		});

		const commonForAll = bridgeUnitTypesPerTooth.reduce(
			(result, perTooth) => result.filter(t => perTooth.includes(t)),
			bridgeUnitTypesPerTooth[0]
		);

		return this.sort(
			commonForAll.map(id => fromUnitTypeInBridge(id)).filter((indication): indication is IntuitiveIndication => indication !== null)
		);
	}

	private isDentureImplantBased(): boolean {
		const procedureId = this.orderQuery.procedure?.Id;
		const procedureMapTypeId = this.orderQuery.procedureMap?.TypeId;

		return isDentureRemovableFullDentureImplantBased(procedureId, procedureMapTypeId);
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	private intuitiveSortOrder = [
		IntuitiveIndication.Crown,
		IntuitiveIndication.Crown_ThreeQuarters,

		IntuitiveIndication.Bridge,

		IntuitiveIndication.Abutment,
		IntuitiveIndication.Pontic,
		IntuitiveIndication.MarylandAbutment,
		IntuitiveIndication.Maryland,

		IntuitiveIndication.VeneerOrLaminates,
		IntuitiveIndication.Veneer,
		IntuitiveIndication.BridgeVeneer,
		IntuitiveIndication.Inlay,
		IntuitiveIndication.BridgeInlay,
		IntuitiveIndication.Onlay,
		IntuitiveIndication.BridgeOnlay,

		IntuitiveIndication.EggshellCrown,
		IntuitiveIndication.EggshellBridge,
		IntuitiveIndication.MockupTooth,
		IntuitiveIndication.MockupBridge,

		IntuitiveIndication.ImplantBased,
		IntuitiveIndication.BridgeImplantBased,
		IntuitiveIndication.ImplantAbutment,
		IntuitiveIndication.BridgeImplantAbutment,
		IntuitiveIndication.ImplantPosition,
		IntuitiveIndication.Clasp,

		IntuitiveIndication.ScanBody,
		IntuitiveIndication.BridgeScanBody,

		IntuitiveIndication.Missing,
		IntuitiveIndication.BridgeMissing,
		IntuitiveIndication.Missing_edentulousSpace,
		IntuitiveIndication.Missing_noSpace,
		IntuitiveIndication.Detachable,
		IntuitiveIndication.ToBeRemoved,

		IntuitiveIndication.SupportingTooth,
		IntuitiveIndication.CerecGuide,

		IntuitiveIndication.Skipped
	];

	private sort(indications: IntuitiveIndication[]): IntuitiveIndication[] {
		return this.intuitiveSortOrder.filter(sortedItem => indications.includes(sortedItem));
	}
}
